import { isChatHelpActionId, type ChatHelpActionId } from "./chat-help";

/** Wire records are transcript evidence; shared item stores own current library state. */
export type ChatItemReference = {
  kind: "stash" | "entity" | "collection" | "tag";
  id: string;
  context?: { kind: string; id: string; collection_id?: string };
};
export type ChatObject = {
  ref: ChatItemReference;
  title: string;
  chat_url: string;
  stasht_url?: string;
  enrichment_description?: string;
};
export type ChatOpener = { text: string; refs: ChatItemReference[]; id?: string; ask?: string };
export type ChatOpeners = {
  schema_version: string;
  generated_at: string;
  expires_at: string;
  prompts: ChatOpener[];
  release_offer?: ChatOpener;
  reason?: string;
  library_state?: string;
};
export type ChatHelp = {
  id?: string;
  actions?: { id: ChatHelpActionId; title: string }[];
  tutorials: { id: string; title: string; platform?: string; video_url: string; open_url?: string }[];
};
export type ChatOperation = {
  operation_id: string;
  action?: string;
  status: string;
  effect?: string;
  items?: { ref: ChatItemReference; title: string }[];
  operation_token?: string;
  requires_confirmation?: boolean;
  expires_at?: number;
  outcomes?: {
    key: string;
    status: string;
    effect?: string;
    entity_id?: string;
    post_id?: string;
    collection_id?: string;
    source_collection_id?: string;
    tag_id?: string;
    hidden?: boolean;
  }[];
  undoable?: boolean;
  background?: boolean;
  phase?: string;
  counts?: Record<string, number>;
  total?: number;
  scope_complete?: boolean;
  destination?: { ref: ChatItemReference; title: string };
  affected?: { collection_ids?: string[]; tag_ids?: string[] };
  cancellable?: boolean;
  error?: string;
};
export type ChatOperationRequest =
  | { action: "apply"; operation_token: string }
  | { action: "resume" | "status" | "cancel"; operation_id: string }
  | { action: "recover"; request_id: string }
  | { action: "undo"; original_operation_id: string; request_id: string; request_started_at: number };
export type ChatRequestContext = {
  schema_version: "stasht.ask_context.v1";
  capabilities: string[];
  request_date: string;
  time_zone: string;
  place?: { latitude: number; longitude: number; observed_at: string };
  daypart: number;
  opener_refs?: ChatItemReference[];
  opener_id?: string;
  opener_ask?: string;
  request_id?: string;
  request_started_at?: number;
  conversation_id?: string;
  turn_id?: string;
  selected_refs?: ChatItemReference[];
  conversation_objects?: ChatObject[];
  previous_operations?: ChatOperation[];
  displayed_release_id?: string;
};
const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
const text = (value: unknown): string | undefined => (typeof value === "string" ? value : undefined);
const strings = (value: unknown) =>
  Array.isArray(value) ? value.filter((s): s is string => typeof s === "string") : [];
const finite = (value: unknown): number | undefined =>
  typeof value === "number" && Number.isFinite(value) ? value : undefined;
function url(value: unknown) {
  try {
    const u = new URL(String(value));
    return ["http:", "https:"].includes(u.protocol) && !u.username && !u.password ? u.href : undefined;
  } catch {
    return undefined;
  }
}
export function readChatReference(value: unknown): ChatItemReference | undefined {
  const ref = record(value),
    context = record(ref.context);
  if (!["stash", "entity", "collection", "tag"].includes(String(ref.kind)) || typeof ref.id !== "string" || !ref.id)
    return;
  const grant =
    typeof context.kind === "string" && typeof context.id === "string" && context.id
      ? {
          kind: context.kind,
          id: context.id,
          ...(typeof context.collection_id === "string" ? { collection_id: context.collection_id } : {}),
        }
      : undefined;
  return { kind: ref.kind as ChatItemReference["kind"], id: ref.id, ...(grant ? { context: grant } : {}) };
}
export function chatReferenceKey(ref: ChatItemReference) {
  return `${ref.kind}:${ref.id}`;
}
export function chatReferenceHref(ref: ChatItemReference) {
  const path = { stash: "stash", entity: "stuff", collection: "collections", tag: "tags" }[ref.kind];
  const collection = ref.context?.kind === "collection" ? ref.context.id : ref.context?.collection_id;
  // Detail navigation accepts collection witnesses for shared stashes and Stuff.
  // A source-stash witness stays in the typed transcript/selection: the ordinary
  // entity-detail endpoint has no source-witness read contract.
  const grant = collection && ref.kind !== "collection" ? `?${new URLSearchParams({ collection })}` : "";
  return `/${path}/${encodeURIComponent(ref.id)}${grant}`;
}
export function readChatObjects(value: unknown): ChatObject[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap(entry => {
    const object = record(entry),
      ref = readChatReference(object.ref);
    if (!ref || typeof object.title !== "string" || typeof object.chat_url !== "string") return [];
    // Match references by their typed identity, never navigate a supplied custom URL.
    const expected = `stasht-item://${ref.kind}/${encodeURIComponent(ref.id)}`;
    if (object.chat_url !== expected) return [];
    return [
      {
        ref,
        title: object.title,
        chat_url: expected,
        stasht_url: url(object.stasht_url),
        enrichment_description: text(object.enrichment_description),
      },
    ];
  });
}
export function readChatOpeners(value: unknown): ChatOpeners {
  const data = record(value);
  if (
    data.schema_version !== "stasht.chat_openers.v1" ||
    typeof data.generated_at !== "string" ||
    typeof data.expires_at !== "string" ||
    !Number.isFinite(Date.parse(data.expires_at)) ||
    !Array.isArray(data.prompts)
  )
    throw new Error("Chat suggestions could not be read. Try refreshing them.");
  const prompt = (value: unknown): ChatOpener | undefined => {
    const p = record(value);
    if (typeof p.text !== "string" || !p.text.trim() || !Array.isArray(p.refs)) return;
    return {
      text: p.text,
      refs: p.refs.flatMap(value => {
        const ref = readChatReference(value);
        return ref ? [ref] : [];
      }),
      id: text(p.id),
      ask: text(p.ask),
    };
  };
  return {
    schema_version: data.schema_version,
    generated_at: data.generated_at,
    expires_at: data.expires_at,
    prompts: data.prompts.flatMap(value => {
      const p = prompt(value);
      return p ? [p] : [];
    }),
    release_offer: prompt(data.release_offer),
    reason: text(data.reason),
    library_state: text(data.library_state),
  };
}
export function readChatHelp(value: unknown): ChatHelp | undefined {
  const help = record(value);
  const tutorials = (Array.isArray(help.tutorials) ? help.tutorials : []).flatMap(entry => {
    const item = record(entry),
      video = url(item.video_url);
    if (typeof item.id !== "string" || typeof item.title !== "string" || !video) return [];
    const u = new URL(video);
    if (
      u.protocol !== "https:" ||
      u.hostname !== "stasht-public-assets.s3.us-east-1.amazonaws.com" ||
      !u.pathname.startsWith("/stasht_app_setup_") ||
      !u.pathname.endsWith(".mp4")
    )
      return [];
    const open = url(item.open_url);
    return [
      {
        id: item.id,
        title: item.title,
        video_url: video,
        platform: text(item.platform),
        open_url: open?.startsWith("https:") ? open : undefined,
      },
    ];
  });
  const actions = Array.isArray(help.actions)
    ? help.actions.flatMap(value => {
        const action = record(value);
        return isChatHelpActionId(action.id) && typeof action.title === "string" && action.title.trim()
          ? [{ id: action.id, title: action.title }]
          : [];
      })
    : undefined;
  const id = text(help.id);
  if (!id && !tutorials.length && !actions?.length) return;
  return { id, tutorials, ...(actions ? { actions } : {}) };
}
export function readChatOperations(value: unknown): ChatOperation[] {
  if (!Array.isArray(value))
    throw new Error("The library change receipt could not be read. Check the result before trying again.");
  return value.map(entry => {
    const raw = record(entry);
    if (typeof raw.operation_id !== "string" || !raw.operation_id || typeof raw.status !== "string")
      throw new Error("The library change receipt could not be read. Check the result before trying again.");
    const item = (value: unknown) => {
      const r = record(value),
        ref = readChatReference(r.ref);
      return ref && typeof r.title === "string" ? { ref, title: r.title } : undefined;
    };
    const affected = record(raw.affected);
    return {
      operation_id: raw.operation_id,
      status: raw.status,
      action: text(raw.action),
      effect: text(raw.effect),
      operation_token: raw.status === "preview" ? text(raw.operation_token) : undefined,
      expires_at: finite(raw.expires_at),
      requires_confirmation: raw.requires_confirmation === true,
      undoable: raw.undoable === true,
      background: raw.background === true,
      phase: text(raw.phase),
      total: finite(raw.total),
      scope_complete: raw.scope_complete === true,
      cancellable: raw.cancellable === true,
      error: text(raw.error),
      items: Array.isArray(raw.items)
        ? raw.items.flatMap(value => {
            const parsed = item(value);
            return parsed ? [parsed] : [];
          })
        : undefined,
      destination: item(raw.destination),
      affected: { collection_ids: strings(affected.collection_ids), tag_ids: strings(affected.tag_ids) },
      counts:
        raw.counts && typeof raw.counts === "object"
          ? Object.fromEntries(
              Object.entries(record(raw.counts)).filter(
                (entry): entry is [string, number] => finite(entry[1]) !== undefined,
              ),
            )
          : undefined,
      outcomes: Array.isArray(raw.outcomes)
        ? raw.outcomes.map(value => {
            const o = record(value);
            if (typeof o.key !== "string" || typeof o.status !== "string")
              throw new Error("The library change receipt could not be read. Check the result before trying again.");
            return {
              key: o.key,
              status: o.status,
              effect: text(o.effect),
              entity_id: text(o.entity_id),
              post_id: text(o.post_id),
              collection_id: text(o.collection_id),
              source_collection_id: text(o.source_collection_id),
              tag_id: text(o.tag_id),
              ...(typeof o.hidden === "boolean" ? { hidden: o.hidden } : {}),
            };
          })
        : undefined,
    };
  });
}
export function mergeChatOperation(previous: ChatOperation | undefined, newer: ChatOperation): ChatOperation {
  return {
    ...newer,
    effect: newer.effect ?? previous?.effect,
    items: newer.items ?? previous?.items,
    operation_token: newer.status === "preview" ? newer.operation_token : undefined,
  };
}
export function chatOperationTitle(operation: ChatOperation) {
  const titles: Record<string, string> = {
    collection_create: "Create a collection",
    collection_add: "Add to a collection",
    collection_remove: "Remove from a collection",
    collection_move: "Move between collections",
    tag_create: "Create a tag",
    tag_add: "Add tags",
    tag_remove: "Remove tags",
    visibility_set: "Change Stuff visibility",
    collection_undo: "Undo collection changes",
    tag_undo: "Undo tag changes",
    visibility_undo: "Undo visibility changes",
  };
  return operation.effect && !operation.action?.endsWith("_undo")
    ? operation.effect
    : titles[operation.action || ""] || "Library change";
}
export function chatOperationStatusSummary(operation: ChatOperation): string | undefined {
  if (operation.error && !(operation.background && operation.counts)) return operation.error;
  if (operation.status === "preview") return undefined;
  if (operation.status === "superseded") return "This preview was replaced. Use the latest reply.";
  const parts: string[] = [];
  if (operation.background && operation.counts) {
    const counts = operation.counts,
      undo = operation.action?.endsWith("_undo");
    if (operation.status === "pending")
      parts.push(
        undo
          ? "Undoing your changes…"
          : operation.phase === "cancelling"
            ? "Stopping remaining changes…"
            : "Working through your request…",
      );
    if (counts.scanned !== undefined)
      parts.push(
        operation.total === undefined
          ? `${counts.scanned} items checked`
          : `${counts.scanned} of ${operation.total} items checked`,
      );
    if (counts.matched) parts.push(`${counts.matched} matched`);
    if (counts.changed) parts.push(`${counts.changed} ${undo ? "changes undone" : "changed"}`);
    if (counts.unchanged) parts.push(`${counts.unchanged} already satisfied`);
    if (counts.uncertain) parts.push(`${counts.uncertain} uncertain, left unchanged`);
    if (!undo && counts.unavailable) parts.push(`${counts.unavailable} couldn’t be checked`);
    const incomplete = undo
      ? Object.entries(counts)
          .filter(([key]) => !["changed", "unchanged", "processed_chunks", "unknown"].includes(key))
          .reduce((sum, [, count]) => sum + count, 0)
      : counts.not_completed || 0;
    if (incomplete) parts.push(`${incomplete} not completed`);
    if (operation.status === "cancelled") parts.push("Stopped. Completed changes are kept.");
    if (operation.phase === "undone") parts.push("Undo finished");
    if (operation.scope_complete) parts.push("Finished checking the requested scope");
    if (operation.status === "unknown") parts.push("Some results still need checking");
    if (operation.error) parts.push(operation.error);
    return (
      parts.join(". ") ||
      (operation.status === "pending" ? "Queued to continue in the background" : "No changes confirmed")
    );
  }
  const outcomes = operation.outcomes || [],
    counts = outcomes.reduce<Record<string, number>>(
      (all, item) => ({ ...all, [item.status]: (all[item.status] || 0) + 1 }),
      {},
    );
  if (counts.changed) parts.push(`${counts.changed} changed`);
  if (counts.unchanged) parts.push(`${counts.unchanged} already satisfied`);
  const incomplete = outcomes.filter(item => !["changed", "unchanged"].includes(item.status)).length;
  if (incomplete) parts.push(`${incomplete} not completed`);
  if (
    operation.status === "pending" ||
    operation.status === "unknown" ||
    (operation.background && operation.status === "failed")
  )
    parts.push("Some results still need checking");
  if (operation.status === "expired") parts.push("The remaining changes expired");
  return parts.join(". ") || "No changes confirmed";
}
export function chatOperationSummary(operation: ChatOperation) {
  return [chatOperationTitle(operation), chatOperationStatusSummary(operation)].filter(Boolean).join(". ");
}
